/**
 * The one HTTP client. Same-origin (`/api` is proxied to the backend by Vite
 * in dev and nginx in the container), so the httpOnly session cookie rides
 * along with no token handling in the browser at all.
 *
 * Every call sends `X-Requested-With`: the backend refuses a state-changing
 * request without it, which is what closes CSRF on top of SameSite=Lax.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** A 422's messages by field path (`name`, `metrics.0.value`), so a form can show each beside its field. */
    public readonly fields: Readonly<Record<string, string>> = {},
    /** The answer's headers: a 409 may name what moved (`X-Asset-Revision`, `X-Refine-Changed`). */
    public readonly headers: Headers = new Headers(),
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

function messageFrom(status: number, payload: unknown): string {
  const detail = (payload as { detail?: unknown } | null)?.detail;
  if (typeof detail === 'string') return detail;
  // FastAPI validation errors: [{ loc, msg }]
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((d: { loc?: unknown[]; msg?: string }) => {
        const field = Array.isArray(d.loc) ? d.loc.slice(1).join('.') : '';
        return field ? `${field}: ${d.msg}` : (d.msg ?? 'Invalid value');
      })
      .join('; ');
  }
  return `Request failed (${status}).`;
}

/** FastAPI validation errors by field path; the body's own name (`body`) is dropped, and the first message wins. */
export function fieldErrorsFrom(payload: unknown): Record<string, string> {
  const detail = (payload as { detail?: unknown } | null)?.detail;
  const fields: Record<string, string> = {};
  if (!Array.isArray(detail)) return fields;
  for (const d of detail as { loc?: unknown[]; msg?: string }[]) {
    const path = Array.isArray(d.loc) ? d.loc.slice(1).join('.') : '';
    if (path && d.msg && !(path in fields)) fields[path] = d.msg;
  }
  return fields;
}

export async function api<T>(path: string, method: Method = 'GET', body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'wynxx-discover',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new ApiError(response.status, messageFrom(response.status, payload), fieldErrorsFrom(payload), response.headers);
    if (response.status === 401) window.dispatchEvent(new CustomEvent('wynxx:unauthorized'));
    throw error;
  }
  return payload as T;
}

/**
 * A file from the API, saved by the browser under the name the server gives
 * (Content-Disposition), e.g. an idea's research as a PDF. Errors read like
 * `api`'s.
 */
export async function apiDownload(path: string, fallbackName: string): Promise<void> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: { 'X-Requested-With': 'wynxx-discover' },
  });
  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    if (response.status === 401) window.dispatchEvent(new CustomEvent('wynxx:unauthorized'));
    throw new ApiError(response.status, messageFrom(response.status, payload), fieldErrorsFrom(payload));
  }
  const name = /filename="([^"]+)"/.exec(response.headers.get('Content-Disposition') ?? '')?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.append(link);
  link.click();
  link.remove();
  // Let the browser start the download before the URL goes.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Send one file as multipart form data (field `file`); errors read like `api`'s.
 *  `tooLarge` is the message for a 413, which nginx may answer itself, with HTML. */
export async function apiUpload<T>(path: string, file: File, tooLarge = 'A file can be at most 10 MB.'): Promise<T> {
  const form = new FormData();
  form.append('file', file, file.name);
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json', 'X-Requested-With': 'wynxx-discover' },
    body: form,
  });
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    if (response.status === 401) window.dispatchEvent(new CustomEvent('wynxx:unauthorized'));
    if (response.status === 413) throw new ApiError(413, tooLarge);
    throw new ApiError(response.status, messageFrom(response.status, payload), fieldErrorsFrom(payload));
  }
  return payload as T;
}
